/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { useContext, type ReactNode } from 'react';
import type { SnippetsSectionProps } from '../Snippets/types';
import { Text } from './Text';
import { SnippetsSection } from '../Snippets/SnippetsSection';
import { Box } from '@mui/material';
import { LogoDownload } from '../../../assets';
import { SpinnerButton } from '../../SpinnerButton/SpinnerButton';
import { SkeletonLoader } from '../SkeletonLoader/SkeletonLoader';
import { SECTION_NAME_DOWNLOAD_PROJECT } from '../../../constants/snippetsSectionNames';
import { OutputBanner } from '../OutputBanner/OutputBanner';
import { InstallationSettingsContext } from '../../../contexts/InstallationSettingsContext';

export function DownloadProject( {
	downloadHandler,
	isDownloadInProgress,
	isLoading,
	number,
	allFeaturesAvailableInPlan,
	arePremiumFeaturesSelected
}: SnippetsSectionProps ): ReactNode {
	const { selectedInstallMethod, selectedCodeOutput } = useContext( InstallationSettingsContext );

	return (
		<SkeletonLoader number={ number } sectionTitle={ SECTION_NAME_DOWNLOAD_PROJECT } isLoading={ isLoading }>
			<SnippetsSection headerCharacter={ number } title={ SECTION_NAME_DOWNLOAD_PROJECT }>
				<OutputBanner
					arePremiumFeaturesSelected={ arePremiumFeaturesSelected }
					allFeaturesAvailableInPlan={ allFeaturesAvailableInPlan }
					installMethod={ selectedInstallMethod }
					codeOutput={ selectedCodeOutput }
				/>
				<Box
					sx={ {
						border: '1px solid',
						borderColor: 'secondary.100',
						justifyContent: 'center',
						textAlign: 'center',
						display: 'flex',
						flexFlow: 'column',
						gap: '10px',
						padding: '20px',
						marginBottom: '20px',
						borderRadius: '5px'
					} }
				>
					<Text>
						Downloading a sample project lets you quickly start developing and testing your interactive editor locally.
					</Text>
					<SpinnerButton
						isLoading={ isDownloadInProgress }
						onClick={ downloadHandler }
						iconSrc={ LogoDownload }
						text={ 'Download sample project' }
					/>
				</Box>
			</SnippetsSection>
		</SkeletonLoader>
	);
}
